// The browser's only way to reach data. Every call goes to the app backend,
// which holds the institutional credential and talks to the Pods. Nothing here
// knows about tokens, WebIDs-as-login, or Comunica — that all lives server-side.

// The session rides on an HttpOnly cookie the browser sends by itself; nothing
// here sees it. A 401 means it ended, which the session module turns into the
// sign-in screen. (Imported lazily: session.svelte.js imports this module.)
async function call(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({ error: `${res.status} ${res.statusText}` }));
  if (res.status === 401 && path !== '/login' && path !== '/me/password') {
    (await import('./session.svelte.js')).sessionEnded();
  }
  // A rejected config carries per-field reasons (server/index.js sends them as
  // `details`). Carry them onto the Error so callers can show which column is
  // wrong instead of "the request failed". A failed multi-write carries `step`
  // (which one broke) and `rollback` (what could not be put back) for the same
  // reason: the screen can only report a phase if the failure names one.
  if (!res.ok) {
    throw Object.assign(new Error(json.error ?? `${res.status} ${res.statusText}`),
      { status: res.status,
        ...(json.details ? { details: json.details } : {}),
        ...(json.step ? { step: json.step } : {}),
        ...(json.rollback ? { rollback: json.rollback } : {}) });
  }
  return json;
}

export const login = (email, password) => call('/login', { method: 'POST', body: { email, password } });
export const logout = () => call('/logout', { method: 'POST' });
export const getMe = () => call('/me');
export const changePassword = (current, next) => call('/me/password', { method: 'POST', body: { current, next } });
export const getInstitution = () => call('/institution');
export const getContext = () => call('/context');
export const getHealth = () => call('/health');
// Pod-resident config: connector | mapping | aggregations | partners | queries |
// institution. Read live from the Pod each call — no build-time import, so a
// curl edit to the Pod shows up on the next fetch with no rebuild.
export const getConfig = (name) => call(`/config/${name}`);
export const getOverview = () => call('/overview');
export const getDatasets = () => call('/datasets');
export const getSchemas = () => call('/schemas');
export const changeSchema = (body) => call('/schemas', { method: 'POST', body });
export const getVocabularies = () => call('/vocabularies');
export const getDashboard = () => call('/dashboard');
export const getDashboardPartners = () => call('/dashboard/partners');
export const rebuildDashboard = async ({ onProgress } = {}) => {
  const { id } = await call('/dashboard/rebuild', { method: 'POST' });
  return pollJob(id, onProgress);
};
export const getRecords = (options = {}) => {
  if (typeof options === 'number') options = { limit: options };
  const query = new URLSearchParams(Object.entries(options).filter(([, value]) => value !== null && value !== '' && value !== undefined));
  return call(`/records${query.size ? `?${query}` : ''}`);
};
export const getCatalog = () => call('/catalog');
// The institution's own private aggregate cubes, reconciled against what is
// declared and what is published. Nothing else in the app can see these.
export const getCubes = () => call('/cubes');
export const getSources = () => call('/sources');
export const getSource = (sourceId) => call(`/sources/${encodeURIComponent(sourceId)}`);
export const saveSource = (source) => call('/sources', { method: 'POST', body: source });
export const createDataset = (body) => call('/datasets', { method: 'POST', body });
export const previewRules = (datasetId, rules) => call(`/datasets/${encodeURIComponent(datasetId)}/rules/preview`, { method: 'POST', body: rules });
export const saveRules = (datasetId, rules) => call(`/datasets/${encodeURIComponent(datasetId)}/rules`, { method: 'PUT', body: rules });
export const getSourceDraft = (datasetId) => call(`/datasets/${encodeURIComponent(datasetId)}/sources`);
export const addSource = (datasetId, body) => call(`/datasets/${encodeURIComponent(datasetId)}/sources`, { method: 'POST', body });
export const datasetRemoval = (id) => call(`/datasets/${encodeURIComponent(id)}/removal`);
export const deleteDataset = (id) => call(`/datasets/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const sourceRemoval = (id) => call(`/sources/${encodeURIComponent(id)}/removal`);
export const deleteSource = (id) => call(`/sources/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const shareEveryTerm = (id) => call(`/datasets/${encodeURIComponent(id)}/cubes`, { method: 'POST' });
export const getDisclosure = (sourceId) => call(`/disclosure/${encodeURIComponent(sourceId)}`);
// The file the source was last given (steward only): the mapping screen opens on it.
export const getStoredSource = (sourceId) => call(`/disclosure/${encodeURIComponent(sourceId)}/file`);
export const saveMapping = (sourceId, mapping) => call(`/disclosure/${encodeURIComponent(sourceId)}/mapping`, { method: 'PUT', body: { mapping } });
export const saveAggregate = (body) => call('/aggregates', { method: 'POST', body });
// What an aggregate would publish, counted over the private records in the Pod
// and written nowhere. Omit `aggregate` to summarise only what already exists.
export const previewAggregates = (body) => call('/aggregates/preview', { method: 'POST', body });
export const deleteAggregate = (id) => call(`/aggregates/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const getCubeAuthoring = () => call('/aggregates/authoring');
// The partner registry with live outbound/inbound probes, for the Partners screen.
export const getPartners = () => call('/partners');
export const getResource = (url) => call(`/resource?url=${encodeURIComponent(url)}`);
export const runQuery = (sparql) => call('/query', { method: 'POST', body: { sparql } });
// Named analysis queries (analyst role, enforced in the backend). Save upserts
// into the Pod's queries.json; delete removes by id.
export const getSuggestions = () => call('/queries/suggestions');
export const saveQuery = (q) => call('/queries', { method: 'POST', body: q });
export const deleteQuery = (id) => call(`/queries/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const publish = (partner) => call('/partners/publish', { method: 'POST', body: partner });

// The pre-run ingest preview: current watermark, pending row count, record count.
export const getIngestPreview = (sourceId) => call(`/ingest?sourceId=${encodeURIComponent(sourceId)}`);

// Poll a backend job (analysis, ingest) to completion. This is what the held
// credential buys us — the work outlives the request, and a slow federated query
// or bulk ingest can't be killed by a browser navigation. `onProgress` receives
// the job each tick, including its `{done, total, message}` progress.
async function pollJob(id, onProgress) {
  for (;;) {
    await new Promise((r) => setTimeout(r, 500));
    const job = await call(`/jobs/${id}`);
    if (job.status === 'done') return job.result;
    if (job.status === 'failed') throw new Error(job.error);
    onProgress?.(job);
  }
}

// Analysis is a background job on the server: start it, then poll.
export async function runAnalysis({ onProgress } = {}) {
  const { id } = await call('/analysis', { method: 'POST' });
  return pollJob(id, onProgress);
}

// Live ingest: start the job (data steward), then poll — progress drives the
// Run-ingest screen's bar.
export async function runIngest(sourceId, { onProgress } = {}) {
  const { id } = await call('/ingest', { method: 'POST', body: { sourceId } });
  return pollJob(id, onProgress);
}
